/** ***********************************************************************
 *
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2019 Adobe Systems Incorporated
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe Systems Incorporated and its suppliers,
 * if any.  The intellectual and technical concepts contained
 * herein are proprietary to Adobe Systems Incorporated and its
 * suppliers and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe Systems Incorporated.
 ************************************************************************* */

import AppConfig from '../stores/config';
import { APPINFO_ACTION_ID } from '../utils/enums';
import { ANALYTICS_CONSTANTS } from '../utils/commonUtil';

class InformationDialogAnalytics {
    setInformationDialogSource(source) {
        this.source = source;
    }

    informationDialogRendered(app) {
        this.sendAnalytics({
            event: {
                workflow: 'Apps',
                subcategory: 'Information',
                type: 'render',
                subtype: 'modal',
            },
            content: {
                id: app.sapcode,
                name: app.appFamilyName,
                category: app.platform,
                version: app.codexVersion,
            },
        });
    }

    informationDialogTabClick(app, tabItem) {
        this.sendAnalytics({
            event: {
                workflow: 'Apps',
                subcategory: 'Information',
                type: 'click',
                subtype: tabItem.tabId,
            },
            content: {
                id: app.sapcode,
                name: app.appFamilyName,
                category: app.platform,
                version: app.codexVersion,
            },
        });
    }

    // TODO: Not used in CCH, CCD to update content.action accordingly
    languageViewMoreClick(app, viewMore) {
        this.sendAnalytics({
            event: {
                workflow: 'Apps',
                subcategory: 'Information:Languages',
                type: 'click',
                subtype: 'manage-settings',
            },
            content: {
                id: app.sapcode,
                name: app.appFamilyName,
                action: viewMore.url,
                category: app.platform,
                version: app.codexVersion,
            },
        });
    }

    versionActionButtonClick(actionID, app, incompatibilityReason) {
        let action;
        switch (actionID) {
        case APPINFO_ACTION_ID.INSTALL:
            action = 'install';
            break;
        case APPINFO_ACTION_ID.UNINSTALL:
            action = 'uninstall';
            break;
        case APPINFO_ACTION_ID.NOT_COMPATIBLE:
            action = 'not-compatible';
            break;
        default:
            break;
        }
        let eventValue = incompatibilityReason;
        if (window.appContext && window.appContext.osLanguage !== 'en') {
            eventValue = ANALYTICS_CONSTANTS.EN_STR_NOT_AVAILABLE;
        }
        this.sendAnalytics({
            event: {
                workflow: 'Apps',
                subcategory: 'Information:Version',
                type: 'click',
                subtype: action,
                value: eventValue,
            },
            content: {
                id: app.sapcode,
                name: app.appFamilyName,
                category: app.platform,
                version: app.codexVersion,
            },
        });
    }

    versionViewMoreClicked(app, viewMore) {
        if (app === undefined) {
            return;
        }

        this.sendAnalytics({
            event: {
                workflow: 'Apps',
                subcategory: 'Information:Version',
                type: 'click',
                subtype: 'older-versions',
            },
            content: {
                id: app.sapcode,
                name: app.appFamilyName,
                action: viewMore.url,
                category: app.platform,
                version: app.codexVersion,
            },
        });
    }

    versionLongTermSupportedClicked(app, lts) {
        if (app === undefined) {
            return;
        }

        // TODO(hkhetan): add analytics according to SDM specs
        this.sendAnalytics({
            event: {
                workflow: 'Apps',
                subcategory: 'Information:Version',
                type: 'click',
            },
            content: {
                id: app.sapcode,
                name: app.appFamilyName,
                action: lts.url,
                category: app.platform,
                version: app.codexVersion,
            },
        });
    }

    sendAnalytics(analyticObject) {
        AppConfig.analyticsCallback({
            analyticObjectList: [analyticObject],
            componentParams: { source: this.source },
        });
    }
}

export default new InformationDialogAnalytics();
